import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Button,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { siteMembersAtom } from '@/interfaces/member';
import { endorsementsAtom } from '@/interfaces/site';
import { assignEndorsementAtom } from '@/interfaces/report';

import type { AssignEndorsementAtomData } from '@/@types/report';

interface AssignEndorsementDialogProps {
  onClose: (reason?: string) => void;
  reportId: string;
  reportName: string;
}
interface EndorserData {
  name: string;
  endorsement: string;
}

export default function AssignEndorsementDialog({ onClose, reportId, reportName }: AssignEndorsementDialogProps) {
  const { data: members } = useAtomValue(siteMembersAtom);
  const { data: endorsements } = useAtomValue(endorsementsAtom);
  const { mutate, isPending, data: isSuccess } = useAtomValue(assignEndorsementAtom);

  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [endorsementId, setEndorsementId] = useState('');
  const [endorsementName, setEndorsementName] = useState('');
  const [endorserList, setEndorserList] = useState<EndorserData[]>([]);
  const [dataList, setDataList] = useState<AssignEndorsementAtomData[]>([]);
  const { t } = useTranslation();

  const autocompleteList = useMemo(
    () => members?.map((member) => ({ id: member.id, label: member.info?.name ?? member.email })),
    [members],
  );

  useEffect(() => {
    if (isSuccess === undefined) return;
    if (isSuccess) {
      onClose('updated');
    } else {
      window.alert(t('Common.message.serverErrorOccured'));
    }
  }, [isSuccess, onClose, t]);

  function onAddClick() {
    setEndorserList([...endorserList, { name: userName, endorsement: endorsementName }]);
    setDataList([...dataList, { reportId, userId, endorsementId }]);

    setUserId('');
    setUserName('');
    setEndorsementId('');
    setEndorsementName('');
  }
  function onAssignClick() {
    for (const data of dataList) {
      mutate(data);
    }
  }

  return (
    <Stack gap={2} sx={{ minWidth: 640, p: 3 }}>
      <DialogTitle sx={{ px: 0 }}>{t('FDA.label.assignEndorsement')}</DialogTitle>
      <Typography>
        {t('Common.label.name')}: {reportName}
      </Typography>
      <Stack direction="row" gap={2}>
        <Autocomplete
          autoHighlight
          size="small"
          options={autocompleteList ?? []}
          value={{ id: userId, label: userName }}
          onChange={(_, item) => {
            setUserId(item?.id ?? '');
            setUserName(item?.label ?? '');
          }}
          renderInput={(params) => <TextField {...params} label={t('FDA.label.endorser')} />}
          sx={{ flexGrow: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('FDA.label.endorsement')}</InputLabel>
          <Select
            label={t('FDA.label.endorsement')}
            value={endorsementId}
            onChange={(e) => setEndorsementId(e.target.value)}
          >
            {endorsements?.map((e) => (
              <MenuItem key={e.id} value={e.id} onClick={() => setEndorsementName(e.name)}>
                {e.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" disabled={userId === '' || endorsementId === ''} onClick={onAddClick}>
          {t('Common.label.add')}
        </Button>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('FDA.label.endorser')}</TableCell>
              <TableCell>{t('FDA.label.endorsement')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {endorserList.map((endorser) => (
              <TableRow key={endorser.name}>
                <TableCell>{endorser.name}</TableCell>
                <TableCell>{endorser.endorsement}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" gap={3}>
        <Button variant="contained" onClick={() => onClose()} sx={{ bgcolor: '#CCC', color: 'black' }}>
          {t('Common.label.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={dataList.length === 0 || isPending}
          onClick={onAssignClick}
          sx={{ bgcolor: 'info.main' }}
        >
          {t('FDA.label.assign')}
        </Button>
      </Stack>
    </Stack>
  );
}
